import { useCallback, useEffect, useState } from 'react';

import {
  ApiError,
  addTaskComment,
  deleteTaskComment,
  editTaskComment,
  listTaskComments,
  type Comment,
  type Visibility,
} from '../api.js';
import { formatDate } from '../labels.js';

/**
 * The conversation on a task.
 *
 * THE VISIBILITY CONTROL IS THE POINT
 * -----------------------------------
 * A comment is internal unless someone says otherwise, and internal ones never
 * leave the company. The server enforces that in the query — this screen only
 * has to make the choice OBVIOUS, because a control nobody notices is a control
 * nobody uses correctly.
 *
 * So: the internal/shared choice sits next to the send button, not in a menu,
 * and every internal comment in the list is visibly labelled. Someone about to
 * write something candid should be able to see, without looking for it, that
 * the client will not read it.
 */

interface Props {
  readonly projectId: string;
  readonly taskId: string;
  readonly onCountChanged: () => void;
}

export function Comments({ projectId, taskId, onCountChanged }: Props): JSX.Element {
  const [comments, setComments] = useState<readonly Comment[] | null>(null);
  const [body, setBody] = useState('');
  const [visibility, setVisibility] = useState<Visibility>('internal');
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setComments(await listTaskComments(projectId, taskId));
    } catch {
      setError('Could not load the comments.');
      setComments([]);
    }
  }, [projectId, taskId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(work: () => Promise<readonly Comment[]>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      setComments(await work());
      onCountChanged();
      return true;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  if (comments === null) return <p className="hint">Loading the conversation…</p>;

  return (
    <div className="comments">
      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      {comments.length === 0 ? (
        <p className="hint">No comments yet.</p>
      ) : (
        <ul className="comment-list">
          {comments.map((comment) => (
            <li key={comment.id} className={`comment vis-${comment.visibility}`}>
              <span className="comment-head">
                <strong>{comment.author_name ?? 'Someone'}</strong>
                <span className="comment-when">
                  {formatDate(comment.created_at)}
                  {/* An edited comment must not look original. */}
                  {comment.edited_at && ' · edited'}
                </span>
                {comment.visibility === 'internal' ? (
                  <span className="vis-badge internal">Internal only</span>
                ) : (
                  <span className="vis-badge shared">Client can see this</span>
                )}
              </span>

              {editing === comment.id ? (
                <>
                  <label className="visually-hidden" htmlFor={`edit-${comment.id}`}>
                    Edit your comment
                  </label>
                  <textarea
                    id={`edit-${comment.id}`}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={2}
                    maxLength={5000}
                    disabled={busy}
                  />
                  <span className="row-actions">
                    <button
                      type="button"
                      className="link-button"
                      disabled={busy || !draft.trim()}
                      onClick={async () => {
                        if (
                          await run(() =>
                            editTaskComment(projectId, taskId, comment.id, {
                              body: draft.trim(),
                              visibility: comment.visibility,
                            }),
                          )
                        ) {
                          setEditing(null);
                        }
                      }}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="link-button"
                      disabled={busy}
                      onClick={() => setEditing(null)}
                    >
                      Cancel
                    </button>
                  </span>
                </>
              ) : (
                <>
                  <span className="comment-body">{comment.body}</span>
                  <span className="row-actions">
                    {/* Editing is offered only on your own — nobody may put
                        words in someone else's mouth, and the server agrees. */}
                    {comment.is_mine && (
                      <button
                        type="button"
                        className="link-button"
                        disabled={busy}
                        onClick={() => {
                          setDraft(comment.body);
                          setEditing(comment.id);
                          setError(null);
                        }}
                      >
                        Edit
                      </button>
                    )}
                    <button
                      type="button"
                      className="link-button danger"
                      disabled={busy}
                      onClick={() =>
                        void run(() => deleteTaskComment(projectId, taskId, comment.id))
                      }
                    >
                      Delete
                    </button>
                  </span>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="comment-compose">
        <label className="visually-hidden" htmlFor={`new-comment-${taskId}`}>
          Add a comment
        </label>
        <textarea
          id={`new-comment-${taskId}`}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={2}
          maxLength={5000}
          disabled={busy}
          placeholder="Add a comment…"
        />

        <div className="compose-actions">
          {/*
            The choice sits next to the send button, not in a menu. Someone
            about to write something candid should see, without looking for it,
            whether the client will read it.
          */}
          <div className="filter-group" role="group" aria-label="Who can see this comment">
            <button
              type="button"
              className={visibility === 'internal' ? 'chip-button current' : 'chip-button'}
              aria-pressed={visibility === 'internal'}
              onClick={() => setVisibility('internal')}
              disabled={busy}
            >
              Internal only
            </button>
            <button
              type="button"
              className={visibility === 'shared' ? 'chip-button current' : 'chip-button'}
              aria-pressed={visibility === 'shared'}
              onClick={() => setVisibility('shared')}
              disabled={busy}
            >
              Client can see
            </button>
          </div>

          <button
            type="button"
            className="inline"
            disabled={busy || !body.trim()}
            onClick={async () => {
              if (
                await run(() =>
                  addTaskComment(projectId, taskId, { body: body.trim(), visibility }),
                )
              ) {
                setBody('');
              }
            }}
          >
            {busy ? 'Posting…' : 'Comment'}
          </button>
        </div>
      </div>
    </div>
  );
}
